import React, { useEffect, useId, useRef, useState } from 'react';
import {
  Dropdown,
  DropdownButton,
  IconWrapper,
  OptionsList,
  OptionItem,
  ChevronWrapper,
  TrailingGroup,
} from './styled-options.styled';
import { Text, font } from '../../style-utils/text';
import { DownArrow } from '../icons/down-arrow';
import { Presence, overlayMotion } from '../../motion';

interface OptionsInputProps {
  options?: string[];
  selectedOption?: string;
  icon: React.ReactNode;
  changeHandler: (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>
      | string,
    fieldName?: string,
    explicitValue?: string
  ) => void;
  changeHandlerName: string;
  isDashboard?: boolean;
  isRsuite?: boolean;
  onOptionSelected?: (value: string) => void;
}

export const OptionsInput: React.FC<OptionsInputProps> = ({
  options,
  selectedOption,
  icon,
  changeHandler,
  changeHandlerName,
  isDashboard,
  isRsuite,
  onOptionSelected,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const controlId = useId();

  const toggleOpen = () => {
    setIsOpen(prev => !prev);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      const optionButtons = Array.from(
        listRef.current?.querySelectorAll<HTMLButtonElement>(
          '[role="option"]'
        ) || []
      );
      const selected = optionButtons.find(
        button => button.getAttribute('aria-selected') === 'true'
      );

      (selected || optionButtons[0])?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  const handleListKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const optionButtons = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ||
        []
    );
    const currentIndex = optionButtons.indexOf(
      document.activeElement as HTMLButtonElement
    );
    let nextIndex = currentIndex;

    if (optionButtons.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % optionButtons.length;
    } else if (event.key === 'ArrowUp') {
      nextIndex =
        (currentIndex - 1 + optionButtons.length) % optionButtons.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = optionButtons.length - 1;
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setIsOpen(false);
      triggerRef.current?.focus();

      return;
    } else {
      return;
    }

    event.preventDefault();
    optionButtons[nextIndex]?.focus();
  };

  return (
    <Dropdown ref={ref} isDashboard={isDashboard || false}>
      <DropdownButton
        ref={triggerRef}
        id={`${controlId}-trigger`}
        type="button"
        isRsuite={isRsuite}
        isDashboard={isDashboard || false}
        onClick={toggleOpen}
        onKeyDown={e => {
          if (e.key === 'Escape') {
            setIsOpen(false);
          } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setIsOpen(true);
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={`${controlId}-options`}
        isOpen={isOpen}
        isSelected={Boolean(selectedOption)}
      >
        <Text
          as="span"
          fontFamily={font.family}
          fontSize={isDashboard ? font.size.xs : font.size.m}
          fontWeight={selectedOption ? 600 : 400}
        >
          {selectedOption
            ? selectedOption
            : isDashboard
            ? 'Select a property'
            : 'Select an option'}
        </Text>
        <TrailingGroup>
          {selectedOption && <IconWrapper>{icon}</IconWrapper>}
          <ChevronWrapper isOpen={isOpen}>
            <DownArrow />
          </ChevronWrapper>
        </TrailingGroup>
      </DropdownButton>
      {/* The listbox grows from the trigger and shrinks back into it: the
          conditional sits inside a Presence so closing plays the exit. */}
      <Presence>
        {isOpen ? (
        <OptionsList
          {...overlayMotion('popover')}
          key="options"
          ref={listRef}
          id={`${controlId}-options`}
          role="listbox"
          aria-labelledby={`${controlId}-trigger`}
          onKeyDown={handleListKeyDown}
          isDashboard={isDashboard || false}
          isRsuite={isRsuite}
        >
          {options?.map((option, index) => (
            <OptionItem
              type="button"
              role="option"
              aria-selected={option === selectedOption}
              tabIndex={
                option === selectedOption || (!selectedOption && index === 0)
                  ? 0
                  : -1
              }
              isDashboard={isDashboard}
              isSelected={option === selectedOption}
              isRsuite={isRsuite}
              key={option}
              onClick={e => {
                if (onOptionSelected) {
                  onOptionSelected(option);
                } else {
                  changeHandler(e, changeHandlerName, option);
                }
                setIsOpen(false);
                triggerRef.current?.focus();
              }}
            >
              {isDashboard ? (
                <IconWrapper>
                  {option === selectedOption ? icon : ''}
                </IconWrapper>
              ) : (
                <div style={{ width: '28px' }}></div>
              )}
              <Text
                as="span"
                fontFamily={font.family}
                fontSize={isDashboard ? font.size.xs : font.size.m}
                fontWeight={option === selectedOption ? 600 : 500}
              >
                {option}
              </Text>
              {!isDashboard && (
                <IconWrapper>
                  {option === selectedOption ? icon : ''}
                </IconWrapper>
              )}
            </OptionItem>
          ))}
        </OptionsList>
        ) : null}
      </Presence>
    </Dropdown>
  );
};
